
import { Publication } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { useTranslation } from 'next-i18next';
import { FiBriefcase, FiCalendar, FiMapPin } from 'react-icons/fi';
import styled from 'styled-components';
import Link from '@/components/molecules/Link';
import { formatTimeAgo, getJobImageUrl } from '@/utilities/functions';

/**
 * Generate URL-friendly slug from publication title
 */
const generateSlug = (title: string): string => {
	return title
		.toLowerCase()
		.trim()
		.replace(/[àáâãäå]/g, 'a')
		.replace(/[èéêë]/g, 'e')
		.replace(/[ìíîï]/g, 'i')
		.replace(/[òóôõö]/g, 'o')
		.replace(/[ùúûü]/g, 'u')
		.replace(/[ç]/g, 'c')
		.replace(/[ñ]/g, 'n')
		.replace(/[^a-z0-9\s-]/g, '') // Remove special characters
		.replace(/\s+/g, '-') // Replace spaces with dashes
		.replace(/-+/g, '-') // Remove multiple dashes
		.replace(/^-|-$/g, ''); // Remove leading/trailing dashes
};

/**
 * Generate locale-aware job URL
 */
const generateJobUrl = (jobId: string, title: string | null | undefined, locale: string): string => {
	const slug = title ? generateSlug(title) : 'offre';

	// Map locale to URL path segments
	const pathSegments: { [key: string]: string } = {
		fr: 'trouver-un-emploi/offre',
		en: 'find-a-job/offer',
		es: 'encontrar-un-empleo/oferta',
	};

	const pathSegment = pathSegments[locale] || pathSegments.fr;
	return `/${pathSegment}/${jobId}/${slug}`;
};

// #region STYLE
const PublicationCardContainer = styled(Link)`
	background: ${({ theme }) => theme.color.white};
	border-radius: 8px;
	overflow: hidden;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	cursor: pointer;
	box-shadow: 0 4px 20px rgba(0,0,0,0.08);
	border: 1px solid ${({ theme }) => theme.color.grey};
	display: flex;
	flex-direction: column;
	text-decoration: none;
	height: 100%;
	
	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 8px 30px rgba(0,0,0,0.12);
	}
`;

const PublicationCardImageContainer = styled.div`
	position: relative;
	width: 100%;
	height: 160px;
	background: linear-gradient(135deg, ${({ theme }) => theme.color.prune} 0%, ${({ theme }) => theme.color.black} 100%);
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
	
	svg {
		width: 48px;
		height: 48px;
		color: ${({ theme }) => theme.color.white};
	}
`;

const PublicationCardImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
	transition: transform 0.3s ease;
	
	${PublicationCardContainer}:hover & {
		transform: scale(1.05);
	}
`;

const PublicationCardOverlay = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: linear-gradient(transparent, rgba(31, 41, 55, 0.8));
	padding: 16px;
	color: ${({ theme }) => theme.color.white};
`;

const PublicationCardTitle = styled.h3`
	font-size: ${({ theme }) => theme.fontSize.LARGE};
	font-weight: 600;
	color: ${({ theme }) => theme.color.white};
	margin: 0;
	line-height: 1.3;
	
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

const PublicationCardContent = styled.div`
	padding: 20px;
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const PublicationCardTimeStamp = styled.div`
	color: ${({ theme }) => theme.color.greyDarker};
	font-size: ${({ theme }) => theme.fontSize.SMALL};
	margin-bottom: 12px;
`;

const PublicationCardMeta = styled.div`
	display: flex;
    flex-direction: column;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
	margin-top: auto;
`;

const PublicationCardMetaItem = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	color: ${({ theme }) => theme.color.black};
	font-size: ${({ theme }) => theme.fontSize.SMALL};
	
	svg {
		width: 14px;
		height: 14px;
		color: ${({ theme }) => theme.color.greyDarker};
		flex-shrink: 0;
	}
	
	span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;
// #endregion

const CONTRACT_TYPE: { [index: string]: string } = {
	CDA: 'Alternance',
	CDD: 'CDD',
	CDI: 'CDI',
	CDP: 'Alternance',
	CTT: 'Intérim',
	STG: 'Stage',
};

const PublicationCard = ({ publication, currentLang = 'fr' }: {
	publication: Recorded<Publication>;
	currentLang?: string;
}) => {
	const { t } = useTranslation(['jobs']);

	const referenceDate = publication.publishedSince ?? publication.createdAt;

	// Get image URL for the publication
	const imageUrl = getJobImageUrl(publication, publication.mission?.job);

	// The saved publication facts own the advertised contract.
	const getContractType = () => {
		if (publication.data?.facts?.category !== undefined) {
			const category = publication.data.facts.category;
			return category ? CONTRACT_TYPE[category] || category : undefined;
		}
		// Check if publication has target data with job type
		if (publication.targetData && 'indeed' in publication.targetData) {
			const jobType = publication.targetData.indeed?.jobType;
			if (jobType) {
				return CONTRACT_TYPE[jobType] || jobType;
			}
		}

		return '?';
	};

	// Get location information
	const getLocation = () => {
		// Check publication address first
		if (publication.address?.townName) {
			const postalCode = publication.address.postalCode;
			return `${publication.address.townName}${postalCode ? ` (${postalCode})` : ''}`;
		}

		// Fallback to mission place
		const mission = publication.mission;
		if (mission?.place?.town) {
			const postalCode = mission.place.postalCode;
			return `${mission.place.town}${postalCode ? ` (${postalCode})` : ''}`;
		}
		return '';
	};

	// Get dates information
	const getDatesInfo = () => {
		const mission = publication.mission;
		if (mission?.startingAt && mission?.endingAt) {
			return mission.startingAt < new Date()
				? `Dès que possible, jusqu'au ${new Date(mission.endingAt).toLocaleDateString('fr-FR', {
					day: 'numeric',
					month: 'numeric',
					year: '2-digit',
				})}`
				: `${new Date(mission.startingAt).toLocaleDateString('fr-FR', {
					day: 'numeric',
					month: 'numeric',
					year: '2-digit',
				})}
				${mission.endingAt
						? ` - ${new Date(mission.endingAt).toLocaleDateString('fr-FR', {
							day: 'numeric',
							month: 'numeric',
							year: '2-digit',
						})}`
						: ''
				}`;
		} else if (mission?.startingAt) {
			return `À partir du ${new Date(mission.startingAt).toLocaleDateString('fr-FR', {
				day: 'numeric',
				month: 'numeric',
				year: '2-digit',
			})}`;
		}
		return '';
	};

	if (!(publication.code)) {
		return null;
	}

	return (
		<PublicationCardContainer href={generateJobUrl(publication.code, publication.title, currentLang)}>
			{imageUrl ? (
				<PublicationCardImageContainer>
					<PublicationCardImage src={imageUrl} alt={publication.title ?? ''} />
					<PublicationCardOverlay>
						<PublicationCardTitle>
							{publication.title}
						</PublicationCardTitle>
					</PublicationCardOverlay>
				</PublicationCardImageContainer>
			) : (
				<PublicationCardImageContainer>
					<FiBriefcase />
					<PublicationCardOverlay>
						<PublicationCardTitle>
							{publication.title}
						</PublicationCardTitle>
					</PublicationCardOverlay>
				</PublicationCardImageContainer>
			)}
			<PublicationCardContent>
				<PublicationCardTimeStamp>
					{referenceDate ? formatTimeAgo(referenceDate, t) : ''}
				</PublicationCardTimeStamp>

				<PublicationCardMeta>
					{getLocation() && (
						<PublicationCardMetaItem>
							<FiMapPin />
							<span>{getLocation()}</span>
						</PublicationCardMetaItem>
					)}
					<PublicationCardMetaItem>
						<FiBriefcase />
						<span>{getContractType()}</span>
					</PublicationCardMetaItem>
					{getDatesInfo() && (
						<PublicationCardMetaItem>
							<FiCalendar />
							<span>{getDatesInfo()}</span>
						</PublicationCardMetaItem>
					)}
				</PublicationCardMeta>
			</PublicationCardContent>
		</PublicationCardContainer>
	);
};

export default PublicationCard;
